import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import useAxios from '@/services/api/useAxios'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import { afterReceivingResourceTimesheetLeave, afterReceiving } from '@/services/timesheet/hooks'

const resource = 'timesheets'

const getTimesheetsByProject = (axios: IFactoryFunctionRequest<any, any>) => (projectId: string): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/project/${projectId}`,
  })
}

const getTimesheetsInterval = (axios: IFactoryFunctionRequest<any, any>) => ({ startDate, endDate, resources }: { startDate: Date; endDate: Date; resources: sting[] }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/interval/${startDate}/${endDate}`,
    data: { resources },
  })
}

const timesheetProjectMonitoring = (axios: IFactoryFunctionRequest<any, any>) => ({ projectId }: { projectId: string }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/engagement/${projectId}/monitoring`,
    afterReceivingHook: afterReceiving
  })
}
const sendReminder = (axios: IFactoryFunctionRequest<any, any>) => ({ timesheetIds = [], timesheetsNotCreated = [] }: { timesheetIds: string[]; timesheetsNotCreated: any[] }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/sendReminder`,
    data: { timesheetIds, timesheetsNotCreated },
  })
}
const updateBooking = (axios: IFactoryFunctionRequest<any, any>) => ({ timesheetId, booking }: { timesheetId: string;booking: { _id: string } }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${timesheetId}/updateBooking/${booking._id}`,
    data: booking,
  })
}

const getTimesheetList = (axios: IFactoryFunctionRequest<any, any>) => (query: { resourceIds: string[]; month; year; search: string; shouldFetchAll: boolean }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    query,
    afterReceivingHook: afterReceivingResourceTimesheetLeave,
  })
}

const updateBookingTimesheet = (axios: IFactoryFunctionRequest<any, any>) => ({ timesheetId, data }: { timesheetId: string;data: { _id: string } }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/updateBookingTimesheet/${timesheetId}/${data._id}`,
    data,
  })
}

const updateTimesheetDetails = (axios: IFactoryFunctionRequest<any, any>) => ({ timesheetId, details }: { timesheetId: string;details: any[] }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/updateDetails/${timesheetId}`,
    data: {details},
  })
}
const createNewTimesheet = (axios: IFactoryFunctionRequest<any, any>) => ({ data }: { data: any }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    data,
    afterReceivingHook: afterReceiving,
  })
}

const closeMonthTimesheets = (axios: IFactoryFunctionRequest<any, any>) => (data: { month: number; year: number; search: string; shouldFetchAll: boolean; jobs?: string[]; jobCategories?: string[]; offices?: string[]; teams?: string[] }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/closeMonth`,
    data,
  })
}

const reopenTimesheet = (axios: IFactoryFunctionRequest<any, any>) => ({ timesheetId }: { timesheetId: string }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${timesheetId}/reopen`,
  })
}

const deleteBooking = (axios: IFactoryFunctionRequest<any, any>) => ({ timesheetId, bookingId }: { timesheetId: string; bookingId: string }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${timesheetId}/deleteBooking/${bookingId}`,
  })
}

const updateTimesheetBookingDays = (axios: IFactoryFunctionRequest<any, any>) => ({ timesheetId, bookingId, data }: { timesheetId: string; bookingId: string; data }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${timesheetId}/${bookingId}/days`,
    data,
  })
}

const getProjectsWaitingForTimesheetValidations = (axios: IFactoryFunctionRequest<any, any>) => (): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/waitingForValidation`,
  })
}

const exportTimeSheetToExcel = (axios: IFactoryFunctionRequest<any, any>) => (query: any): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export`,
    query,
    immediate: false,
  })
}

const updateComment = (axios: IFactoryFunctionRequest<any, any>) => ({ timesheetId, comment }: { timesheetId: string; comment: string }): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${timesheetId}/comment`,
    data: { comment },
  })
}

export default (axios: AxiosInstance) => ({
  getTimesheetsByProject: getTimesheetsByProject(useAxios(axios)),
  getTimesheetsInterval: getTimesheetsInterval(useAxios(axios)),
  timesheetProjectMonitoring: timesheetProjectMonitoring(useAxios(axios)),
  updateBooking: updateBooking(useAxios(axios)),
  sendReminder: sendReminder(useAxios(axios)),
  getTimesheetList: getTimesheetList(useAxios(axios)),
  createNewTimesheet: createNewTimesheet(useAxios(axios)),
  closeMonthTimesheets: closeMonthTimesheets(useAxios(axios)),
  reopenTimesheet: reopenTimesheet(useAxios(axios)),
  deleteBooking: deleteBooking(useAxios(axios)),
  updateTimesheetBookingDays: updateTimesheetBookingDays(useAxios(axios)),
  updateBookingTimesheet: updateBookingTimesheet(useAxios(axios)),
  updateTimesheetDetails: updateTimesheetDetails(useAxios(axios)),
  getProjectsWaitingForTimesheetValidations: getProjectsWaitingForTimesheetValidations(useAxios(axios)),
  exportTimeSheetToExcel: exportTimeSheetToExcel(useAxios(axios)),
  updateComment: updateComment(useAxios(axios)),
})
